<template>
  <v-container
    fluid
    class="text--primary"
    style="min-height: 590px"
  >
    <input-dialog
      v-model="widgetSubdomain"
      :label-title="$t('label.booking_url')"
      :visible="urlEditorVisible"
      :message="$t('message.enter_booking_form_url')"
      confirm-title="save"
      max-width="400"
      :textfield-suffix="widgetDomain"
      rules="alpha_num|min:3|max:50|unique_widget_url"
      no-auto-close
      debounce-validation="300"
      @confirm="saveURLEditor"
      @cancel="closeURLEditor"
    />

    <h1>{{ $t('label.congratulations') }} 🎉</h1>
    <p class="py-2 text-subtitle-1">
      {{ $t('message.setup_finished') }}
    </p>
    <v-row>
      <v-col cols="12">
        <h3 class="mb-2">
          {{ $t('title.online_reservations') }}
        </h3>
        {{ $t('label.hint_booking_page') }}
      </v-col>
      <v-col
        cols="12"
        class="ma-0 pa-0"
      >
        <v-col
          cols="12"
          class="pt-0 mt-0"
        >
          <v-sheet
            :color="$vuetify.theme.dark ? 'secondary lighten-2' : 'grey lighten-3'"
            class="pt-1 text-center text-body-1"
          >
            <a
              :href="hostedFormUrl"
              target="_blank"
            >{{ hostedFormUrl }}</a>
            <v-icon
              small
              class="pl-1 pb-1"
            >
              mdi-open-in-new
            </v-icon>
            <div>
              <v-btn
                class="mt-2 mb-3 blue white--text"
                x-small
                outlined
                @click="openURLEditor"
              >
                <v-icon
                  small
                  class="mr-1"
                >
                  mdi-pencil
                </v-icon>
                {{ $t('button.change_url_address') }}
              </v-btn>
            </div>
          </v-sheet>
        </v-col>
      </v-col>

      <v-col cols="12">
        <h3 class="mb-2 pt-1">
          {{ $t('label.bookings_from_your_website') }}
        </h3>
        {{ $t('label.hint_bookings_from_website') }}
      </v-col>
      <v-col
        cols="12"
        class="my-0 pt-0 pb-5"
      >
        <v-btn
          outlined
          target="_blank"
          block
          color="primary"
          :href="widgetDocumentationURL"
        >
          {{ $t('button.discover_widget') }}
        </v-btn>
      </v-col>

      <v-col
        cols="12"
        class=""
      >
        <h3 class="mb-2">
          {{ $t('label.learn_more_about_guestplan') }}
        </h3>
        {{ $t('label.hint_learn_more_about_guestplan') }}
      </v-col>
      <v-col
        cols="12"
        class="my-0 pt-0 pb-3"
      >
        <v-btn
          block
          outlined
          color="primary"
          href="https://docs.guestplan.com"
          target="_blank"
        >
          <v-icon>mdi-book</v-icon>{{ $t('button.guestplan_manual') }}
        </v-btn>
      </v-col>
    </v-row>
  </v-container>
</template>

<script lang="ts">
import { hostedWidgetURL, WIDGET_DOMAIN } from '@/services/configuration';
import Campaign from '@/grpc-api/model/campaign';
import Vue from 'vue';
import { performAction } from '@/services/vue-utils';
import grpcClient from '@/grpc-api/grpc-client';
import InputDialog from '@/components/dialogs/InputDialog.vue';
import { cloneModel } from '@/model/model-utils';
import { extend } from 'vee-validate';
import { ApiErrorCode } from '@/api/api-error';

export default Vue.extend({
  name: 'WelcomeStepOnline',
  components: { InputDialog },
  props: { },
  data() {
    return {
      widgetSubdomain: null as string | null,
      campaign: null as Campaign | null,
      urlEditorVisible: false,
    };
  },
  computed: {
    widgetDomain(): string {
      return `.${WIDGET_DOMAIN}`;
    },
    hostedFormUrl(): string {
      const subdomain = this.campaign?.subdomain;
      return subdomain ? hostedWidgetURL({ subdomain }) : this.$tstore.getters.hostedWidgetURL ?? 'N/A';
    },
    widgetDocumentationURL(): string {
      return this.$tstore.state.settings.account.widgetDocumentationURL || '';
    },
  },
  watch: { },
  mounted() {
    extend('unique_widget_url', {
      validate: async (value, _) => {
        if (this.campaign === null || value === undefined) return true;
        const nc = cloneModel(this.campaign);
        nc.subdomain = value;
        return this.testCampaign(nc);
      },
      lazy: true,
    });

    // this.reset();
  },
  methods: {
    // eslint-disable-next-line vue/no-unused-properties
    async reset() {
      this.campaign = null;
      this.widgetSubdomain = null;
      if (!this.$tstore.getters.isLoaded) return;

      await this.loadCampaign();
    },

    async openURLEditor() {
      const ok = await this.loadCampaign();
      if (ok) this.urlEditorVisible = true;
    },
    closeURLEditor() {
      this.urlEditorVisible = false;
    },
    async saveURLEditor() {
      if (!this.campaign) { this.urlEditorVisible = false; return; }

      const nc = cloneModel(this.campaign);
      nc.subdomain = this.widgetSubdomain ?? undefined;

      const ok = await this.updateCampaign(nc);
      if (ok) this.urlEditorVisible = false;
    },
    async loadCampaign(): Promise<boolean> {
      const ok = await performAction(
        null,
        this.$i18n.tc('error.load_data'),
        async () => {
          this.campaign = await grpcClient.getDefaultCampaign();
          this.widgetSubdomain = this.campaign.subdomain ?? null;
        },
      );
      return ok;
    },
    async testCampaign(c: Campaign): Promise<string|boolean> {
      try {
        await grpcClient.testDefaultCampaign(c);
      } catch (e: any) {
        if (e.code === ApiErrorCode.already_exists || e.code === ApiErrorCode.subdomain_already_exists) {
          return this.$i18n.tc('error.url_already_taken');
        }
        if (e.code === ApiErrorCode.invalid_argument || e.code === ApiErrorCode.subdomain_invalid) {
          return this.$i18n.tc('error.url_not_allowed');
        }
        return true;
      }
      return true;
    },
    async updateCampaign(c: Campaign): Promise<boolean> {
      const ok = await performAction(
        null,
        this.$i18n.tc('error.save_data'),
        async () => {
          this.campaign = await grpcClient.updateDefaultCampaign(c);
        },
      );
      return ok;
    },
  },
});
</script>
